/**
 * Bookable-candidate query hooks (ADR-0019).
 *
 * Server-orchestrated reads with no FHIR collection behind them, so they live in
 * the app rather than in `@repo/data-store` — there is nothing to project and
 * nothing to optimistically mutate. Candidates are deliberately not cached: one
 * is stale the moment it is handed out, and `accept` re-decides it server-side.
 *
 * Replaces `use-find-availability.ts`, which is gone: its candidates were
 * segment-shaped and its procedures no longer exist.
 */

import type {
  BookableCandidateDto,
  FindConsultationCandidatesParams,
  FindCuratedSessionCandidatesParams,
  FindProtocolVisitCandidatesParams,
  FindRescheduleCandidatesParams,
  ProtocolVisitCandidateDto,
} from "@repo/api";
import { getTRPCClient } from "@repo/api";
import { useCallback, useState } from "react";

export interface CandidateQueryResult<TInput, TCandidate = BookableCandidateDto> {
  candidates: TCandidate[];
  isLoading: boolean;
  error: string | null;
  query: (input: TInput) => Promise<TCandidate[]>;
  reset: () => void;
}

/**
 * The three kinds of work differ only in what they name — after resolution they
 * are one thing and one availability search serves all three, so the hook is
 * one implementation parameterized by the procedure that answers.
 */
function useCandidateQuery<TInput, TCandidate = BookableCandidateDto>(
  fetch: (input: TInput) => Promise<{ success: true; data: TCandidate[] } | { success: false }>,
): CandidateQueryResult<TInput, TCandidate> {
  const [candidates, setCandidates] = useState<TCandidate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useCallback(
    async (input: TInput) => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await fetch(input);
        // Narrowed rather than cast: the router wraps every reply in a
        // success/error union, and the old hooks asserted their way past it.
        const found = result.success ? result.data : [];
        setCandidates(found);
        return found;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to find availability");
        setCandidates([]);
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [fetch],
  );

  const reset = useCallback(() => {
    setCandidates([]);
    setIsLoading(false);
    setError(null);
  }, []);

  return { candidates, isLoading, error, query, reset };
}

const findProtocolVisit = (input: FindProtocolVisitCandidatesParams) =>
  getTRPCClient().booking.findProtocolVisitCandidates.query(input);
const findConsultation = (input: FindConsultationCandidatesParams) =>
  getTRPCClient().booking.findConsultationCandidates.query(input);
const findCuratedSession = (input: FindCuratedSessionCandidatesParams) =>
  getTRPCClient().booking.findCuratedSessionCandidates.query(input);
const findReschedule = (input: FindRescheduleCandidatesParams) =>
  getTRPCClient().booking.findRescheduleCandidates.query(input);

export function useFindProtocolVisitCandidates(): CandidateQueryResult<
  FindProtocolVisitCandidatesParams,
  ProtocolVisitCandidateDto
> {
  return useCandidateQuery(findProtocolVisit);
}

export function useFindConsultationCandidates(): CandidateQueryResult<FindConsultationCandidatesParams> {
  return useCandidateQuery(findConsultation);
}

export function useFindCuratedSessionCandidates(): CandidateQueryResult<FindCuratedSessionCandidatesParams> {
  return useCandidateQuery(findCuratedSession);
}

/**
 * Options for moving a visit that is already booked. The visit is the whole
 * input — its work, its clinician and its length are already facts, and the
 * server adds back the reservations the move will release so the visit can be
 * offered times near the one it currently holds.
 */
export function useFindRescheduleCandidates(): CandidateQueryResult<FindRescheduleCandidatesParams> {
  return useCandidateQuery(findReschedule);
}
